/**
 * Rich text (or plain text) to a single line of readable plain text.
 *
 * Violation bodies, comments and other WYSIWYG columns live in DB columns that hold two shapes —
 * plaintext from the legacy textarea editors, HTML from the rich text editor — and nothing normalizes
 * them on write. Anywhere the app shows that content as text rather than rendering it (list previews,
 * CSV exports) it has to flatten it first, or the user sees `<p>`, `<b>` and `&nbsp;`.
 *
 * This is the TypeScript twin of the server-side CsvExportService::plainText(); the two are kept in
 * the same order deliberately, so the same row reads identically in the app and in an export.
 *
 * Normalizes only — truncation and CSV quoting stay with the caller.
 */

/**
 * Elements whose CONTENTS are markup rather than prose. Removing only the tags leaves a cell or a
 * preview holding a stylesheet: a Gmail or Outlook-web paste carries an uncommented `<style>` block,
 * and a whole-document paste carries `<title>`. Word's own paste wraps its stylesheet in an HTML
 * comment, which is why HTML_COMMENTS covered this until the uncommented form turned up.
 */
const RAW_TEXT_ELEMENTS = /<(style|script|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/**
 * Tags whose boundaries carry meaning as whitespace — without this, removing tags fuses the words on
 * either side ("<p>one</p><p>two</p>" would become "onetwo"). Matched raw and entity-encoded, since
 * stored bodies contain both. Same boundary rule as HTML_TAGS, because pasted email threads collide
 * with these names too: without it, `<p.balster@hotmail.com>` (a real production row) reads as a
 * <p> tag and the address vanishes.
 */
const BLOCK_TAGS = /(<|&lt;)\/?(br|p|div|li|ul|ol|tr|td|th|table|h[1-6]|blockquote)(?=[\s\/>]|&gt;)[^>]*?(>|&gt;)/gi;

/**
 * A complete tag whose name is a real HTML element.
 *
 * The element names are listed rather than inferred, parsing is done with a regex rather than the DOM,
 * and a name only counts when followed by a space, "/" or ">" — each guard exists because a looser
 * rule deletes text a customer typed. A generic <word> match eats the letter-template placeholders
 * that survive into violation bodies (`Dear <NAME>, the issue <ISSUE> has been resolved`). An HTML
 * parser treats a bare "<" as an open tag and drops everything after it ("temp <50 degrees ok"
 * becomes "temp "). And pasted email threads carry addresses in angle brackets
 * (`From: Martin Sukadi <n.starratt@comcast.net>`), so without the boundary check
 * `<b.smith@example.com>` reads as a bold tag and vanishes.
 */
const HTML_TAGS = /<\/?(?:a|abbr|article|aside|b|blockquote|body|br|button|caption|center|code|col|colgroup|dd|del|div|dl|dt|em|figcaption|figure|font|footer|form|h[1-6]|head|header|hr|html|i|img|input|ins|label|li|link|meta|nav|ol|option|p|pre|s|script|section|select|small|span|strike|strong|style|sub|sup|table|tbody|td|textarea|tfoot|th|thead|title|tr|u|ul)(?=[\s\/>])[^>]*>/gi;

const HTML_COMMENTS = /<!--[\s\S]*?-->/g;

/**
 * Namespaced markup that Word and Outlook paste in (`<o:p>`, `<v:shapetype …>` — both in production
 * rows). A colon in the tag name is the signal, with one carve-out: the same Outlook paste also
 * produces `<mailto:ssmcghie@gmail.com>`, so a match may never cross an `@` — Office namespace tags
 * contain no addresses, mailto artifacts always do.
 */
const NAMESPACED_TAGS = /<\/?[a-z]+:[a-z][^>@]*>/gi;

/**
 * Private-use-area code points: font-specific glyphs that paste in from Word and Outlook and render
 * as tofu boxes in a spreadsheet. Carried over from the CSV encoder this replaced.
 */
const PRIVATE_USE_AREA = /[\uE000-\uF8FF\u{F0000}-\u{FFFFD}\u{100000}-\u{10FFFD}]/gu;

export function toPlainText(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const stripped = String(value)
            .replace(RAW_TEXT_ELEMENTS, '')
            .replace(BLOCK_TAGS, ' ')
            .replace(HTML_COMMENTS, '')
            .replace(NAMESPACED_TAGS, '')
            .replace(HTML_TAGS, '');

    return decodeEntities(stripped)
            .replace(PRIVATE_USE_AREA, '')
            .replace(/\u00A0/g, ' ')
            .replace(/[\r\n\t]+/g, ' ')
            .replace(/ {2,}/g, ' ')
            .trim();
}

/**
 * Whether a stored body is HTML rather than plaintext, deciding whether a view renders it or runs it
 * through `nl2br`.
 *
 * Uses the same element allow-list and boundary rule as the flattener above, and for the same
 * reasons: a bare `/<\/?[a-z][^>]*>/i` reads `From: Martin Sukadi <n.starratt@comcast.net>` as HTML,
 * so the address is handed to `[innerHTML]`, dropped by Angular's sanitizer, and the body's line
 * breaks collapse because `nl2br` was skipped. `Dear <NAME>,` fails the same way.
 */
export function containsHtmlMarkup(value: string | null | undefined): boolean {
    if (!value) {
        return false;
    }

    const text = String(value);

    return oneShot(BLOCK_TAGS).test(text)
            || oneShot(HTML_TAGS).test(text)
            || oneShot(NAMESPACED_TAGS).test(text);
}

/**
 * A non-global copy of a pattern. The constants above carry /g for `replace()`, and a /g regex keeps
 * `lastIndex` between `test()` calls — so sharing them here would make the answer depend on the
 * previous value tested.
 */
function oneShot(pattern: RegExp): RegExp {
    return new RegExp(pattern.source, pattern.flags.replace('g', ''));
}

/**
 * Decode entities via a textarea: its content is raw text, so this decodes every entity — named or
 * numeric — without parsing tags, and cannot turn an encoded fragment back into a live one. Runs
 * after tag removal for that reason, the same order the server-side formatter uses.
 */
function decodeEntities(value: string): string {
    if (value.indexOf('&') === -1) {
        return value;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;

    return textarea.value;
}
